/**
 * Semantic theme for styled-components & V2 layout (colors, type, spacing, sidebar/header).
 * Used by atoms/molecules and pages — import from `design-system/theme` or `../theme/semanticTheme` inside DS.
 * Primitive tokens: `design-system/tokens.js`. MUI bridge: `theme/muiTheme.js`.
 */
export const theme = {
  colors: {
    primary: "#0e121d",
    secondary: "#2a3140",
    text: "#181e2a",
    textMuted: "#585e6d",
    border: "#dfe1e6",
    background: "#ffffff",
    backgroundAlt: "#f8f9fb",

    purple: "#390095",
    purpleLight: "#6630d2",
    green: "#1b963a",
    blue: "#1d48e6",
    orange: "#f08d0e",
    red: "#e82c35",
    dark: "#0e121d",

    accent: "#390095",
    accentLight: "rgba(57, 0, 149, 0.059)",
    success: "#1b963a",
    successLight: "rgba(27, 150, 58, 0.078)",
    error: "#e82c35",
    errorLight: "rgba(232,44,53,0.08)",
    /** Status / “processing” pills (yellow-amber; readable with white label text) */
    warning: "#ca8a04",
    warningLight: "rgba(202,138,4,0.08)",
    info: "#1d48e6",
  },
  typography: {
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontSize: {
      xs: "11px",
      sm: "12px",
      md: "13px",
      base: "14px",
      lg: "16px",
      xl: "20px",
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.2,
      normal: 1.4,
      relaxed: 1.5,
    },
    pageHeading: {
      fontSize: "20px",
      fontWeight: 600,
      lineHeight: 1.2,
    },
    sectionHeading: {
      fontSize: "16px",
      fontWeight: 600,
      lineHeight: 1.3,
    },
    inputLabel: {
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: 1.4,
    },
    inputValue: {
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: 1.4,
    },
    placeholder: {
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: 1.4,
    },
    body: {
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: 1.4,
    },
    bodySmall: {
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: 1.4,
    },
    caption: {
      fontSize: "11px",
      fontWeight: 400,
      lineHeight: 1.4,
    },
    tableHeader: {
      fontSize: "11px",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    tableCell: {
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: 1.4,
    },
    statusBadge: {
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: 1.2,
      /** Uniform pill width for short statuses (ACTIVE / INACTIVE / PENDING); longer labels still expand. */
      minWidth: "5.75rem",
    },
  },
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "24px",
    xxl: "32px",
  },
  borderRadius: {
    sm: "4px",
    md: "8px",
    lg: "12px",
  },
  breakpoints: {
    xs: "480px",
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
  },
  header: {
    height: "64px",
    background: "rgba(255, 255, 255, 0.75)",
    borderBottom: "1px solid rgba(223, 225, 230, 0.8)",
  },
  sidebar: {
    background: "#ffffff",
    borderRight: "1px solid #dfe1e6",
    activeBg: "rgba(57, 0, 149, 0.08)",
    activeBorder: "#390095",
    hoverBg: "rgba(57, 0, 149, 0.04)",
  },
};

export default theme;
